import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class MessagingService {
  currentMessage = new BehaviorSubject(null);
  constructor(private angularFireMessaging: AngularFireMessaging) {
    this.angularFireMessaging.messaging.subscribe(
      (_messaging) => {
        _messaging.onMessage = _messaging.onMessage.bind(_messaging);
        _messaging.onTokenRefresh = _messaging.onTokenRefresh.bind(_messaging);
      }
    );
  }

  async requestPermission(): Promise<string> {
    try {
      if (!('Notification' in window)) {
        return '';
      }
      if (!('serviceWorker' in navigator)) {
        return '';
      }

      if (Notification.permission === 'denied') {
        return '';
      }

      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return '';
        }
      }

      const existing = sessionStorage.getItem('fcmToken');
      if (existing) {
        return existing;
      }

      const token = await new Promise<string>((resolve) => {
        let swListener: ((event: MessageEvent) => void) | null = null;

        swListener = (event: MessageEvent) => {
          if (event.data) {
            if (event.data.type === 'FCM_TOKEN' && event.data.token) {
              navigator.serviceWorker.removeEventListener('message', swListener!);
              resolve(event.data.token);
            }
          }
        };

        navigator.serviceWorker.addEventListener('message', swListener);

        const poll = async () => {
          try {
            const t = await this.readTokenFromDB();
            if (t) {
              navigator.serviceWorker.removeEventListener('message', swListener!);
              resolve(t);
            } else {
              setTimeout(poll, 500);
            }
          } catch (e) {
            setTimeout(poll, 500);
          }
        };

        const registration = navigator.serviceWorker.getRegistration();
        if (registration) {
          registration.then((reg) => {
            if (reg && reg.active) {
              reg.active.postMessage({ type: 'GET_TOKEN' });
              poll();
            } else {
              resolve('');
            }
          }).catch(() => {
            resolve('');
          });
        } else {
          resolve('');
        }

        setTimeout(() => {
          navigator.serviceWorker.removeEventListener('message', swListener!);
          resolve('');
        }, 20000);
      });

      if (token) {
        sessionStorage.setItem('fcmToken', token);
      }
      return token;
    } catch (err) {
      return '';
    }
  }

  receiveMessage() {
    // The service worker's own 'push' listener already calls showNotification() for every
    // incoming message (see firebase-messaging-sw.js) - the native OS/browser popup is shown
    // exactly once, there. Both listeners below exist only to update in-app state
    // (currentMessage); neither should construct its own Notification() for the same event,
    // or the same push ends up displayed a second (or third) time.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (!event.data) return;
        if (event.data.type === 'FCM_TOKEN' && event.data.token) {
          sessionStorage.setItem('fcmToken', event.data.token);
        } else if (event.data.type === 'FCM_MESSAGE') {
          this.currentMessage.next(event.data.payload);
        }
      });
    }
    this.angularFireMessaging.messages.subscribe(
      (payload) => {
        this.currentMessage.next(payload);
      });
  }

  private readTokenFromDB(): Promise<string> {
    return new Promise((resolve) => {
      const request = indexedDB.open('fcm-token-db', 1);
      request.onupgradeneeded = (e: any) => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('tokens')) {
          db.createObjectStore('tokens');
        }
      };
      request.onsuccess = (e: any) => {
        try {
          const db = (e.target as IDBOpenDBRequest).result;
          const tx = db.transaction('tokens', 'readonly');
          const store = tx.objectStore('tokens');
          const getReq = store.get('fcm_token');
          getReq.onsuccess = () => {
            resolve(getReq.result || '');
          };
          getReq.onerror = () => resolve('');
        } catch (e) {
          resolve('');
        }
      };
      request.onerror = () => resolve('');
    });
  }
}
